import axios from 'axios'
import qs from 'qs'
import {getPreferredLanguageFromCookie} from "@/util/locale";

axios.defaults.baseURL = process.env.VUE_APP_JFORCE_API_ENDPOINT
axios.defaults.withCredentials = process.env.NODE_ENV === 'development'
axios.defaults.headers.common['Accept-Language'] = getPreferredLanguageFromCookie()
const paramsSerializer = (params) => {
  return qs.stringify(params, { arrayFormat: 'repeat' })
}

// Instance to call JForce Express Api
const expressJForceInstance = axios.create({
  baseURL: process.env.VUE_APP_GIT_BRANCH
    ? process.env.VUE_APP_JFORCE_EXPRESS_ENDPOINT +
      process.env.VUE_APP_GIT_BRANCH
    : process.env.VUE_APP_JFORCE_EXPRESS_ENDPOINT
})

// instance for mock api
const mockInstace = axios.create({
  baseURL:
    process.env.VUE_APP_MOCK_API == 'true'
      ? 'https://jumia.mocklab.io'
      : process.env.VUE_APP_JFORCE_API_ENDPOINT
})

// Payment Methods APi mapping
const paymentMethodsTypes = {
  bankAccount: 'bank_transfer',
  mobileMoneyAccount: 'mobile_money',
  voucher: 'voucher'
}

const user = {
  create: ({ data }) => axios.post(`consultants`, data),
  update: ({ consultantId }, { data }) =>
    axios.put(`ewoks/consultants/${consultantId}`, data),
  acceptAgreement: ({ consultantId }) =>
    axios.put(`ewoks/consultants/${consultantId}/agreement`, {
      jforce_agreement: true
    }),

  authUrl: () => axios.get(`auth-url`),
  logoutUrl: () => axios.post(`logout-url`),

  // User authentication Api calls
  login: (code, email, referral, state) => {
    return axios.post(`login`, { code, email, referral, state })
  },

  // user image
  getImage: (data) => {
    const formData = new FormData()
    formData.append('file', data)
    return axios.post(`ewoks/upload-image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  // JForce Token
  setJForceToken: (token) => {
    axios.defaults.headers.common['X-Jforce-Token'] = token
  },
}

const team = {
  getOneTeamMember: ({ consultantId, teamMemberId }) =>
    axios.get(`ewoks/consultants/${consultantId}/team-members/${teamMemberId}`),
  getAllTeamMembers: ({ consultantId }) =>
    axios.get(`ewoks/consultants/${consultantId}/team-members`),
  getTeamLeader: ({ consultantId }) =>
    axios.get(`ewoks/consultants/${consultantId}/my-team-leader`),
  getTeamMembersTotal: ({ consultantId }) =>
    axios.get(`ewoks/consultants/${consultantId}/team-members/total`),
  getSales: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/sales/team-members/total`, {
      params
    }),
  getTeamMemberSalesHistory: ({ consultantId, teamMemberId }, { params }) =>
    axios.get(
      `watto/consultants/${consultantId}/sales/team-members/total-by-periods/${teamMemberId}`,
      { params }
    ),
  inviteTeamMember: ({ consultantId }, { data }) =>
    axios.post(`ewoks/consultants/${consultantId}/invite-user`, data)
}

const sales = {
  getTotal: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/sales/total`, { params }),
  getTotalByStatus: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/sales/by-status`, { params }),
  getTotalByCategory: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/sales/by-category`, {
      params
    })
}

const period = {
  getAll: ({ params }) =>
    axios.get(`watto/periods/all`, { params })
}

const commissions = {
  getCommissions: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/commissions/total`, {
      params
    }),
  getCommissionsGroupedByType: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/commissions/grouped-by-type`, {
      params
    }),
  getCommissionsByService: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/commissions/total-services`, {
      params
    }),
  getCommissionsGroupedByCategory: ({ consultantId }, { params }) =>
    axios.get(
      `watto/consultants/${consultantId}/commissions/grouped-by-category`,
      { params }
    ),
  getCommissionsGroupedByStatus: ({ consultantId }, { params }) =>
    axios.get(
      `watto/consultants/${consultantId}/commissions/grouped-by-status`,
      { params }
    )
}

const config = {
  getConfigurations: () => axios.get(`ewoks/voucher-payment-threshold`)
}

const orders = {
  getAll: ({ consultantId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/orders`, {
      params,
      paramsSerializer
    }),
  getOne: ({ consultantId, orderId }, { params }) =>
    axios.get(`watto/consultants/${consultantId}/order/${orderId}`, {
      params,
      paramsSerializer
    }),
  getAvailableFilters: ({ consultantId }) =>
    axios.get(`watto/consultants/${consultantId}/orders/filters`),
  getSuccessRates: ({ consultantId }, { params }) =>
    axios.get(
      `watto/consultants/${consultantId}/orders-success-rate-by-period`,
      { params }
    )
}

const bonuses = {
  getAll: ({ consultantId }, { params }) =>
    axios.get(`mandalorian/consultants/${consultantId}/bonuses`, { params }),
  getHomepageBonuses: ({ consultantId }, { params }) =>
    axios.get(`mandalorian/consultants/${consultantId}/homepage-bonuses`, { params }),
  getOne: ({ consultantId, bonusId }, { params }) =>
    axios.get(`mandalorian/consultants/${consultantId}/bonuses/${bonusId}`, {
      params
    }),
  getMandalorianBonusesGracePeriod: () =>
      axios.get(`mandalorian/bonuses/grace-period`, {})
}

const tempMandalorian = {
  format: ({ params }) =>
    axios.get(`mandalorian/temp/format`, { params }),
}

const notifications = {
  getAll: ({ consultantId }, { params }) =>
    axios.get(`kazuda/notifications/${consultantId}`, {
      params,
      paramsSerializer
    }),
  subscribe: ({ consultantId, payload }) =>
    axios.post(`kazuda/notifications/${consultantId}/subscribe`, {
      subscription: JSON.parse(payload)
    }),
  unsubscribe: ({ consultantId, payload }) =>
    axios.delete(`kazuda/notifications/${consultantId}/un-subscribe`, {
      data: { subscription: JSON.parse(payload) }
    }),
  unsubscribeAll: ({ consultantId, payload }) =>
    axios.delete(`kazuda/notifications/${consultantId}/un-subscribe-all`, {
      payload
    }),
  redirect: ({ consultantId, notificationId }) =>
    axios.get(
      `kazuda/notifications/${consultantId}/redirect/${notificationId}`
    )
}

const levels = {
  getOne: ({ levelId }) => axios.get(`ewoks/levels/${levelId}`)
}

const locations = {
  getAll: ({ params }) => axios.get(`ewoks/sky-states`, { params }),
  getCities: (stateId, { params }) =>
    axios.get(`ewoks/sky-states/${stateId}/sky-cities`, { params })
}

const banks = {
  getAll: ({ params }) => axios.get(`ewoks/banks`, { params })
}

const recruitmentChannels = {
  getAll: ({ params }) => axios.get(`ewoks/recruitment-channels`, { params })
}

const languages = {
  getAll: ({ params }) => axios.get(`ewoks/languages`, { params })
}

const networks = {
  getAll: ({ params }) => axios.get(`ewoks/networks`, { params })
}

const timeSlots = {
  getAll: ({ params }) => axios.get(`ewoks/time-slots`, { params })
}

const jforceTypes = {
  getAll: ({ params }) => axios.get(`ewoks/jforce-types`, { params })
}

const paymentMethods = {
  getAllAvailable: () => axios.get('ewoks/available-payment-methods'),
  getAll: ({ consultantId }) =>
    axios.get(`ewoks/consultants/${consultantId}/payment-methods`),
  create: ({ consultantId, paymentType }, { data }) =>
    axios.post(
      `ewoks/consultants/${consultantId}/payment-methods/${paymentMethodsTypes[paymentType]}`,
      data
    ),
  update: ({ consultantId, paymentType, paymentMethodId }, { data }) =>
    axios.put(
      `ewoks/consultants/${consultantId}/payment-methods/${paymentMethodsTypes[paymentType]}/${paymentMethodId}`,
      data
    )
}

const staticBlocks = {
  getOne: ({ blockCode }) => axios.get(`mouse/blocks/${blockCode}`)
}

const homepageBlocks = {
  get: () => axios.get(`mouse/homepage`)
}

const shortLinks = {
  create: ({ url, consultant_id }) => axios.post('ewoks/short-links', { url, consultant_id })
}

const getSRconfigs = {
  getSRconfigsByLevel: ({ levelId }) =>
    axios.get(`watto/success-rate-configs/${levelId}`)
}

export {
  team,
  sales,
  orders,
  commissions,
  config,
  levels,
  locations,
  period,
  banks,
  networks,
  jforceTypes,
  timeSlots,
  languages,
  recruitmentChannels,
  user,
  paymentMethods,
  staticBlocks,
  homepageBlocks,
  getSRconfigs,
  notifications,
  bonuses,
  tempMandalorian,
  shortLinks
}
